/**
 * Deposit platform fee — v1.0
 *
 * MIRROR of _compute_deposit_platform_fee() in backend/server.py. Keep the two
 * in lockstep; if they ever disagree, the SERVER is authoritative.
 *
 * Use this ONLY to preview a price before an agreement exists (e.g. the builder's
 * live preview, where nothing has been stamped yet). Once an agreement exists,
 * read the stored price off GET /agreements/{id}/fee-status instead — a
 * historical agreement keeps the price it was sold at and must never be
 * repriced client-side.
 */

export const DEPOSIT_FEE_VERSION = '1.0';

const SLAB_1_LIMIT = 15000000; // ₹1,50,000 in paise
const SLAB_1_RATE = 0.007;     // 0.7% on the first ₹1,50,000
const SLAB_2_RATE = 0.010;     // 1.0% on the excess
const MIN_PAISE = 25000;       // ₹250 floor
const MAX_PAISE = 2500000;     // ₹25,000 cap

export const DEPOSIT_FEE_GST_RATE = 0.18;

/**
 * Platform fee in paise, EXCLUDING GST, for a deposit of `depositPaise`.
 * Returns 0 for a zero/absent deposit — the ₹250 floor applies only to a
 * deposit Orello actually holds.
 */
export function depositPlatformFeePaise(depositPaise) {
  const deposit = Math.max(0, Math.round(Number(depositPaise) || 0));
  if (deposit <= 0) return 0;

  const slab1 = Math.min(deposit, SLAB_1_LIMIT);
  const slab2 = Math.max(0, deposit - SLAB_1_LIMIT);
  let fee = slab1 * SLAB_1_RATE + slab2 * SLAB_2_RATE;

  // Clamp first, then round to whole rupees — both bounds are whole rupees, so
  // rounding can never carry the result back outside them.
  fee = Math.max(MIN_PAISE, Math.min(MAX_PAISE, fee));
  return Math.round(fee / 100) * 100;
}

/** 18% GST on a platform fee. Separate line item; never commissionable. */
export function depositFeeGstPaise(feePaise) {
  return Math.round((Number(feePaise) || 0) * DEPOSIT_FEE_GST_RATE);
}

/** { fee, gst, total } in paise for a deposit — total is what the tenant is charged. */
export function depositFeeBreakdown(depositPaise) {
  const fee = depositPlatformFeePaise(depositPaise);
  const gst = depositFeeGstPaise(fee);
  return { fee, gst, total: fee + gst };
}
